import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MdAutoAwesome, MdCheck, MdRefresh, MdSave } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { AsyncSearchableSelect } from '../../../shared/layout/forms/AppSelect';
import {
  autoMapAdminOrganizationCategories,
  fetchAdminOrganizationCategoryMappings,
  searchAdminOrganizationCategoryTaxonomy,
  updateAdminOrganizationCategoryMapping,
} from '../../../services/Http/AdminApi';

const Toolbar = styled.div`display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin:12px 0;`;
const Group = styled.div`display:flex;align-items:center;gap:8px;flex-wrap:wrap;`;
const Button = styled.button`
  display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:36px;padding:0 11px;
  border:1px solid ${({ $primary }) => ($primary ? '#176b57' : '#cbd5d1')};border-radius:5px;
  background:${({ $primary }) => ($primary ? '#176b57' : '#fff')};color:${({ $primary }) => ($primary ? '#fff' : '#26332f')};
  font-weight:600;cursor:pointer;&:disabled{opacity:.55;cursor:not-allowed;}
`;
const Filter = styled.select`min-height:36px;padding:0 28px 0 9px;border:1px solid #cbd5d1;border-radius:5px;background:#fff;color:#26332f;`;
const Summary = styled.div`display:flex;gap:18px;flex-wrap:wrap;padding:10px 0;color:#47534f;border-bottom:1px solid #e2e8e5;`;
const TableWrap = styled.div`overflow:auto;width:100%;`;
const Table = styled.table`
  width:100%;min-width:850px;border-collapse:collapse;font-size:13px;
  th{text-align:left;font-size:11px;color:#68756f;background:#f6f8f7;padding:9px;border-bottom:1px solid #dce3df;}
  td{padding:10px 9px;border-bottom:1px solid #e7ece9;vertical-align:middle;}
`;
const CategoryText = styled.div`font-weight:600;overflow-wrap:anywhere;`;
const Secondary = styled.div`margin-top:3px;color:#738079;font-size:11px;`;
const Status = styled.span`
  display:inline-flex;padding:4px 7px;border-radius:4px;font-size:11px;font-weight:600;
  color:${({ $done }) => ($done ? '#176b57' : '#805800')};background:${({ $done }) => ($done ? '#e6f4ee' : '#fff4d6')};
`;
const Notice = styled.p`margin:10px 0;color:#56645e;line-height:1.4;`;
const Message = styled.p`margin:10px 0;color:${({ $error }) => ($error ? '#a52828' : '#176b57')};`;
const Empty = styled.div`padding:24px 8px;color:#68756f;text-align:center;`;

const optionFor = (category) => category ? {
  value: String(category.gpt_code),
  label: `${category.path} (${category.gpt_code})`,
} : null;

const AdminOrganizationCategoryMappings = ({ organizationId }) => {
  const { t } = useTranslation();
  const [payload, setPayload] = useState({ mappings: [], sources: [], summary: {} });
  const [sourceType, setSourceType] = useState('');
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(false);
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const data = await fetchAdminOrganizationCategoryMappings(organizationId, sourceType ? { source_type: sourceType } : {});
      setPayload(data);
      setSelected(Object.fromEntries((data.mappings || []).map((row) => [row.id, optionFor(row.category)])));
    } catch (error) {
      setMessage({ error: true, text: error.response?.data?.message || 'Could not load category mappings.' });
    } finally {
      setLoading(false);
    }
  }, [organizationId, sourceType]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => payload.mappings || [], [payload.mappings]);

  const save = async (row) => {
    const target = selected[row.id];
    if (!target?.value) return;
    setSaving((current) => ({ ...current, [row.id]: true }));
    setMessage(null);
    try {
      await updateAdminOrganizationCategoryMapping(organizationId, row.id, target.value);
      setMessage({ text: t('CategoryMappings.saved') });
      await load();
    } catch (error) {
      setMessage({ error: true, text: error.response?.data?.message || t('CategoryMappings.saveFailed') });
    } finally {
      setSaving((current) => ({ ...current, [row.id]: false }));
    }
  };

  const runAiMapping = async () => {
    setMapping(true);
    setMessage(null);
    try {
      const result = await autoMapAdminOrganizationCategories(organizationId, sourceType || undefined);
      setMessage({ text: t('CategoryMappings.aiDone', result) });
      await load();
    } catch (error) {
      setMessage({ error: true, text: error.response?.data?.message || t('CategoryMappings.aiFailed') });
    } finally {
      setMapping(false);
    }
  };

  const loadOptions = async (query) => {
    if (!query || query.trim().length < 2) return [];
    try {
      const result = await searchAdminOrganizationCategoryTaxonomy(organizationId, query.trim());
      return (result.categories || []).map(optionFor);
    } catch (_) {
      return [];
    }
  };

  return (
    <>
      <Notice>{t('CategoryMappings.description')}</Notice>
      <Toolbar>
        <Group>
          <Filter value={sourceType} onChange={(event) => setSourceType(event.target.value)} aria-label={t('CategoryMappings.sourceFilter')}>
            <option value="">{t('CategoryMappings.allSources')}</option>
            {(payload.sources || []).map((source) => <option key={source} value={source}>{source}</option>)}
          </Filter>
          <Button type="button" onClick={load} disabled={loading} title={t('CategoryMappings.refresh')} aria-label={t('CategoryMappings.refresh')}><MdRefresh size={17} /></Button>
        </Group>
        <Button type="button" $primary onClick={runAiMapping} disabled={mapping || loading || !(payload.summary?.ai_available > 0)}>
          <MdAutoAwesome size={17} />{mapping ? t('CategoryMappings.aiRunning') : t('CategoryMappings.aiAction')}
        </Button>
      </Toolbar>
      <Summary>
        <span>{t('CategoryMappings.total')}: <strong>{payload.summary?.total || 0}</strong></span>
        <span>{t('CategoryMappings.confirmed')}: <strong>{payload.summary?.confirmed || 0}</strong></span>
        <span>{t('CategoryMappings.review')}: <strong>{payload.summary?.to_review || 0}</strong></span>
      </Summary>
      {message && <Message role={message.error ? 'alert' : 'status'} $error={message.error}>{message.text}</Message>}
      {loading ? <Empty>{t('CategoryMappings.loading', { defaultValue: 'Loading…' })}</Empty> : rows.length === 0 ? (
        <Empty>{t('CategoryMappings.empty')}</Empty>
      ) : (
        <TableWrap>
          <Table>
            <thead><tr>
              <th>{t('CategoryMappings.source')}</th>
              <th>{t('CategoryMappings.external')}</th>
              <th>{t('CategoryMappings.target')}</th>
              <th>{t('CategoryMappings.status')}</th>
              <th>{t('CategoryMappings.action')}</th>
            </tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id}>
                <td>{row.source_type}</td>
                <td><CategoryText>{row.external_category_path || row.external_category_name || row.external_category_id}</CategoryText><Secondary>{t('CategoryMappings.externalId')}: {row.external_category_id}</Secondary></td>
                <td>
                  <AsyncSearchableSelect
                    cacheOptions
                    defaultOptions={false}
                    loadOptions={loadOptions}
                    value={selected[row.id] || null}
                    onChange={(_, value, option) => setSelected((current) => ({ ...current, [row.id]: option || (value ? { value, label: value } : null) }))}
                    placeholder={t('CategoryMappings.searchTaxonomy')}
                    aria-label={`${t('CategoryMappings.target')}: ${row.external_category_path}`}
                  />
                  {row.category && <Secondary>GPT {row.category.gpt_code}{row.mapping_source === 'gemini' && row.mapping_confidence !== null ? ` · ${row.mapping_confidence}%` : ''}</Secondary>}
                </td>
                <td><Status $done={row.mapping_status === 'confirmed'}>{t(`CategoryMappings.status_${row.mapping_status}`, { defaultValue: row.mapping_status })}</Status></td>
                <td><Button type="button" onClick={() => save(row)} disabled={!selected[row.id]?.value || saving[row.id]}>
                  {row.mapping_status === 'confirmed' ? <MdCheck size={16} /> : <MdSave size={16} />}
                  {saving[row.id] ? t('CategoryMappings.saving') : t('CategoryMappings.save')}
                </Button></td>
              </tr>
            ))}</tbody>
          </Table>
        </TableWrap>
      )}
    </>
  );
};

export default AdminOrganizationCategoryMappings;
